import { Box, Avatar, Typography } from '@mui/material';
import { getNameInitials } from '@utils/regex';
import { getFormattedDistance } from '@utils/date';
import { MessagesSearchResponse } from '@lio/api-types';

export interface MessageSearchResultItemProps {
  message: MessagesSearchResponse['items'][number];
  onClick: (noteId: string) => void;
  searchTerm: string;
}

function extractPlain(message: any): string {
  if (message.rawText) return message.rawText;
  if (!message.body) return '';
  try {
    const parsed = JSON.parse(message.body);
    if (parsed?.blocks?.length) return parsed.blocks.map((b: any) => b.text).join('\n');
  } catch (e) {
    return message.body;
  }
  return message.body;
}

export const MessageSearchResultItem = ({ message, searchTerm, onClick }: MessageSearchResultItemProps) => {
  const name = `${message.createdByUser?.firstName ?? ''} ${(message as any).createdByUser?.lastName ?? ''}`.trim();
  const initials = getNameInitials(name || 'NA');
  const plain = extractPlain(message);
  const idx = plain.toLowerCase().indexOf(searchTerm.toLowerCase());
  let snippet = plain;
  if (idx >= 0) {
    const start = Math.max(0, idx - 40);
    const end = Math.min(plain.length, idx + searchTerm.length + 60);
    snippet = (start > 0 ? '…' : '') + plain.slice(start, end) + (end < plain.length ? '…' : '');
  }
  const timeStamp = getFormattedDistance(new Date(message.createdAt));

  const highlighted =
    idx >= 0 ? (
      <Typography variant='bodySmall'>
        {snippet.split(new RegExp(`(${searchTerm})`, 'ig')).map((part, i) => (
          <Box
            key={i}
            component='span'
            sx={
              part.toLowerCase() === searchTerm.toLowerCase()
                ? { fontWeight: 600, backgroundColor: 'warning.light' }
                : undefined
            }
          >
            {part}
          </Box>
        ))}
      </Typography>
    ) : (
      <Typography variant='bodySmall'>{snippet}</Typography>
    );

  const isLeft = message.createdByUser?.lioUsers?.length;

  return (
    <Box
      onClick={() => {
        onClick(message.id);
      }}
      sx={{
        display: 'flex',
        borderRadius: 8,
        gap: 1,
        alignItems: 'flex-start',
        p: 1.5,
        margin: 1,
        cursor: 'pointer',
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 3px 7px rgba(0, 0, 0, 0.2)',
        },
        backgroundColor: 'common.white',
      }}
    >
      <Avatar sx={{ width: 32, height: 32, bgcolor: isLeft ? 'primary.dark' : 'accent.secondary' }}>{initials}</Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant='subtitle2'>{name || 'Unknown User'}</Typography>
        {highlighted}
      </Box>
      <Typography variant='caption' sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
        {timeStamp}
      </Typography>
    </Box>
  );
};
